"use client";

import { Badge } from "@package/subframe/components/Badge";
import { DropdownMenu } from "@package/subframe/components/DropdownMenu";
import { IconButton } from "@package/subframe/components/IconButton";
import { IconWithLogoBackground } from "@package/subframe/components/IconWithLogoBackground";
import { Table } from "@package/subframe/components/Table";
import {
  FeatherMoreHorizontal,
  FeatherPencil,
  FeatherUsers,
} from "@subframe/core";
import * as SubframeCore from "@subframe/core";

import { Spinner } from "@/components/ui/spinner";
import { TABLE_MIN_WIDTH } from "@/components/ui/table-scroll-container";
import { MemberDto, MemberDtoStatus } from "@/generated/model";

interface MembersTabProps {
  data: MemberDto[];
  isLoading: boolean;
  error: unknown;
}

export function MembersTab({
  data: members,
  isLoading,
  error,
}: MembersTabProps) {
  if (isLoading) {
    return (
      <div className="flex w-full items-center justify-center py-12">
        <Spinner className="h-8 w-8 text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex w-full flex-col items-center justify-center py-12 gap-4">
        <IconWithLogoBackground variant="error" size="x-large" />
        <div className="flex flex-col items-center gap-2">
          <span className="text-heading-3 font-heading-3 text-default-font">
            Error loading members
          </span>
          <span className="text-body font-body text-subtext-color">
            Please try again later.
          </span>
        </div>
      </div>
    );
  }

  if (members.length === 0) {
    return (
      <div className="flex w-full flex-col items-center justify-center py-12 gap-4 rounded-md border border-solid border-secondary-beige-200">
        <IconWithLogoBackground
          variant="neutral"
          size="x-large"
          icon={<FeatherUsers />}
        />
        <div className="flex flex-col items-center gap-2">
          <span className="text-heading-3 font-heading-3 text-default-font">
            No members found
          </span>
          <span className="text-body font-body text-subtext-color text-center max-w-md">
            Invite members to join your nursery group.
          </span>
        </div>
      </div>
    );
  }

  const getStatusBadge = (status: MemberDtoStatus) => {
    switch (status) {
      case MemberDtoStatus.ACTIVE:
        return <Badge variant="success">Active</Badge>;
      case MemberDtoStatus.INACTIVE:
        return <Badge variant="neutral">Inactive</Badge>;
      default:
        return <Badge variant="neutral">{status}</Badge>;
    }
  };

  return (
    <div className="flex w-full flex-col items-start bg-default-background overflow-auto">
      <Table
        className={TABLE_MIN_WIDTH.md}
        header={
          <Table.HeaderRow>
            <Table.HeaderCell>Email</Table.HeaderCell>
            <Table.HeaderCell>Role</Table.HeaderCell>
            <Table.HeaderCell>Status</Table.HeaderCell>
            <Table.HeaderCell>Joined</Table.HeaderCell>
            <Table.HeaderCell className="items-center justify-end">
              Actions
            </Table.HeaderCell>
          </Table.HeaderRow>
        }
      >
        {members.map((member) => (
          <Table.Row key={member.id}>
            <Table.Cell>
              <span className="text-body-bold font-body-bold text-default-font">
                {member.userEmail}
              </span>
            </Table.Cell>
            <Table.Cell>
              <span className="text-body font-body text-default-font capitalize">
                {member.role === "ADMIN"
                  ? "Group Administrator"
                  : member.role.toLowerCase()}
              </span>
            </Table.Cell>
            <Table.Cell>{getStatusBadge(member.status)}</Table.Cell>
            <Table.Cell>
              <span className="text-body font-body text-subtext-color">
                {new Date(member.joinedAt).toLocaleDateString()}
              </span>
            </Table.Cell>
            <Table.Cell variant="aligned-right">
              <div className="flex items-center justify-end gap-2">
                <SubframeCore.DropdownMenu.Root>
                  <SubframeCore.DropdownMenu.Trigger asChild={true}>
                    <IconButton
                      variant="neutral-primary"
                      size="medium"
                      icon={<FeatherMoreHorizontal />}
                    />
                  </SubframeCore.DropdownMenu.Trigger>
                  <SubframeCore.DropdownMenu.Portal>
                    <SubframeCore.DropdownMenu.Content
                      side="bottom"
                      align="end"
                      sideOffset={4}
                      asChild={true}
                    >
                      <DropdownMenu>
                        <DropdownMenu.DropdownItem icon={<FeatherPencil />}>
                          Manage
                        </DropdownMenu.DropdownItem>
                      </DropdownMenu>
                    </SubframeCore.DropdownMenu.Content>
                  </SubframeCore.DropdownMenu.Portal>
                </SubframeCore.DropdownMenu.Root>
              </div>
            </Table.Cell>
          </Table.Row>
        ))}
      </Table>
    </div>
  );
}
